import DOMPurify from 'dompurify';

export interface ThreadBodyMention {
	label: string;
	userId: string;
}

export interface RenderThreadBodyHtmlOptions {
	currentUserId?: string;
	mentions?: ThreadBodyMention[];
}

interface NormalizedMention extends ThreadBodyMention {
	className: string;
}

// Capture URLs for split(), then exclude trailing prose punctuation from each link.
const URL_SPLIT_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;
const MENTION_CLASS = 'support-thread-mention';
const SELF_MENTION_CLASS = `${MENTION_CLASS}--self`;
const TEAMMATE_MENTION_CLASS = `${MENTION_CLASS}--teammate`;
const IMAGE_PREVIEW_CLASS = 'support-thread-image-preview';
const MENTION_TRAILING_BOUNDARY = '[\\s\\u00a0.,;:!?\\)\\]\\}]';

// Sanitize before DOM transforms because callers inject the result as HTML.
export function renderThreadBodyHtml(body: string, options: RenderThreadBodyHtmlOptions = {}): string {
	const template = document.createElement('template');
	template.innerHTML = DOMPurify.sanitize(body);
	linkifyTextNodes(template.content);
	annotateMentionTextNodes(template.content, options.mentions ?? [], options.currentUserId);
	annotateImagesForPreview(template.content);
	for (const anchor of template.content.querySelectorAll('a[href]')) {
		anchor.setAttribute('target', '_blank');
		anchor.setAttribute('rel', 'noopener noreferrer');
	}
	return template.innerHTML;
}

function linkifyTextNodes(root: DocumentFragment): void {
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	// Collect first: replacing nodes while the TreeWalker is iterating would skip siblings.
	const textNodes = collectTextNodes(walker);
	for (const textNode of textNodes) {
		if (textNode.parentElement?.closest('a')) continue;
		const parts = (textNode.textContent ?? '').split(URL_SPLIT_PATTERN);
		if (parts.length === 1) continue;
		const replacement = document.createDocumentFragment();
		parts.forEach((part, index) => {
			if (index % 2 === 1) {
				const trailing = part.match(TRAILING_PUNCTUATION)?.[0] ?? '';
				const url = trailing ? part.slice(0, -trailing.length) : part;
				const anchor = document.createElement('a');
				anchor.setAttribute('href', url);
				anchor.textContent = url;
				replacement.appendChild(anchor);
				if (trailing) replacement.appendChild(document.createTextNode(trailing));
			} else if (part) {
				replacement.appendChild(document.createTextNode(part));
			}
		});
		textNode.replaceWith(replacement);
	}
}

function annotateMentionTextNodes(
	root: DocumentFragment,
	mentions: ThreadBodyMention[],
	currentUserId: string | undefined
): void {
	const normalizedMentions = normalizeMentions(mentions, currentUserId);
	if (normalizedMentions.length === 0) return;

	const mentionsByLabel = new Map(normalizedMentions.map(mention => [mention.label, mention]));
	const mentionPattern = new RegExp(
		`(^|[^\\w@.])@(${normalizedMentions.map(mention => escapeRegExp(mention.label)).join('|')})(?=$|${MENTION_TRAILING_BOUNDARY})`,
		'g'
	);
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const textNodes = collectTextNodes(walker);

	for (const textNode of textNodes) {
		if (textNode.parentElement?.closest(`a, .${MENTION_CLASS}`)) continue;
		const text = textNode.textContent ?? '';
		mentionPattern.lastIndex = 0;
		if (!mentionPattern.test(text)) continue;

		mentionPattern.lastIndex = 0;
		const replacement = document.createDocumentFragment();
		let lastIndex = 0;
		for (const match of text.matchAll(mentionPattern)) {
			const fullMatch = match[0];
			const prefix = match[1] ?? '';
			const label = match[2] ?? '';
			const mention = mentionsByLabel.get(label);
			if (!mention || match.index === undefined) continue;

			const mentionStart = match.index + prefix.length;
			const mentionEnd = match.index + fullMatch.length;
			if (mentionStart > lastIndex) {
				replacement.appendChild(document.createTextNode(text.slice(lastIndex, mentionStart)));
			}
			const mentionSpan = document.createElement('span');
			mentionSpan.className = mention.className;
			mentionSpan.textContent = text.slice(mentionStart, mentionEnd);
			replacement.appendChild(mentionSpan);
			lastIndex = mentionEnd;
		}
		if (lastIndex === 0) continue;
		if (lastIndex < text.length) {
			replacement.appendChild(document.createTextNode(text.slice(lastIndex)));
		}
		textNode.replaceWith(replacement);
	}
}

function annotateImagesForPreview(root: DocumentFragment): void {
	for (const image of root.querySelectorAll('img')) {
		image.classList.add(IMAGE_PREVIEW_CLASS);
		image.setAttribute('role', 'button');
		image.setAttribute('tabindex', '0');
		image.setAttribute('aria-label', 'Open image preview');
	}
}

function normalizeMentions(mentions: ThreadBodyMention[], currentUserId: string | undefined): NormalizedMention[] {
	const mentionsByLabel = new Map<string, NormalizedMention>();
	for (const mention of mentions) {
		const label = mention.label.trim().replace(/^@+/, '');
		if (!mention.userId || !label) continue;
		const key = label.toLowerCase();
		const existing = mentionsByLabel.get(key);
		const className = `${MENTION_CLASS} ${mention.userId === currentUserId ? SELF_MENTION_CLASS : TEAMMATE_MENTION_CLASS}`;
		if (!existing || mention.userId === currentUserId) {
			mentionsByLabel.set(key, { ...mention, className, label });
		}
	}

	return [...mentionsByLabel.values()].sort((a, b) => b.label.length - a.label.length);
}

function collectTextNodes(walker: TreeWalker): Text[] {
	const textNodes: Text[] = [];
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		textNodes.push(node as Text);
	}
	return textNodes;
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
